import { useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import { useMemo } from 'react'
import { 
        // fetchMemberships,
        addPaymentSetupIntent,
        // addPayIntent,
        deletePaymentMethod,
        // fetchPaymentMethods,
     } from '../api/crud'

import { useGetPaymentMethods } from './memberhooks'
import { createConnectedAccount, getConnectedAccount, CreatePaymentAccountSchema } from '../api/stripeConnect'


const staleTime = 5 * 60 * 1000 // 5 minutes
const gcTime = 10 * 60 * 1000 // 10 minutes

// export const useGetPaymentMethods = (uid: string) => {
//     const {data: paymentMethods, refetch: reloadPaymentMethods, isLoading: isLoadingPaymentMethods} = useQuery({ 
//         queryKey: ['payMethods', uid], 
//         queryFn: async () => {
//             console.log("fetching payMethods")
//             const res = await fetchPaymentMethods(uid)
//             return res ? res : []
//         },
//         staleTime,
//         enabled: isEnabled
//     })

//     const getPaymentMethod = (account: AccountSchema) => {
//         let paymentMethodId = account.payment_method
//         return paymentMethods.find((obj: any) => obj['id'] === paymentMethodId);
//     }

//     return { isLoadingPaymentMethods, paymentMethods, getPaymentMethod, reloadPaymentMethods}
// }

export const useGetConnectedAccount = (communityId: string, userId: string) => {
    const queryClient = useQueryClient()
    // Memoize the parameters to prevent unnecessary re-renders
    const memoizedCommunityId = useMemo(() => communityId, [communityId])
    const memoizedUserId = useMemo(() => userId, [userId])
    
    // Only enable the query if both parameters are valid
    const isEnabled = useMemo(() => 
        Boolean(memoizedCommunityId && memoizedUserId && 
                memoizedCommunityId.trim() !== '' && 
                memoizedUserId.trim() !== ''), 
        [memoizedCommunityId, memoizedUserId]
    )

    const { data: connectedAccount, isLoading: isLoadingAccountData, refetch: reloadAccountData } = useQuery({
        queryKey: ['connectedAccount', memoizedCommunityId, memoizedUserId],
        queryFn: () => getConnectedAccount(memoizedCommunityId, memoizedUserId),
        staleTime,
        gcTime,
        enabled: isEnabled,
        retry: 2, // Limit retries to prevent excessive calls
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
    })

    const createPaymentAccountMutation = useMutation({
        mutationFn: async (data: CreatePaymentAccountSchema) => {
            const res = await createConnectedAccount(data)
            return res
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({
                queryKey: ['connectedAccount', variables.communityId, variables.userId],
            })
        },
    })

    const createPaymentAccount = (data: CreatePaymentAccountSchema) => {
        return createPaymentAccountMutation.mutateAsync(data)
    }
    const isLoadingPaymentAccount = createPaymentAccountMutation.isPending
    const errorPaymentAccount = createPaymentAccountMutation.error
    const isSuccessPaymentAccount = createPaymentAccountMutation.isSuccess
    const isErrorPaymentAccount = createPaymentAccountMutation.isError

    return { 
        accountId: connectedAccount?.accountId ?? '',
        accountData: connectedAccount?.accountData ?? null,
        isLoadingAccountData, 
        isLoadingPaymentAccount,
        errorPaymentAccount,
        isSuccessPaymentAccount,
        isErrorPaymentAccount,
        createPaymentAccount,
        reloadAccountData 
    }
}


export const useAddPaySetupIntent = (uid: string) => {
    const { reloadPaymentMethods } = useGetPaymentMethods(uid)

    const mutation = useMutation({ 
        mutationFn: async () => {
            console.log("add setup intent")
            const res = await addPaymentSetupIntent(uid)
            // console.log("addPaymentSetupIntent res: ", res)
            const {client_secret} = res
            // await reloadPaymentMethods()
            return client_secret
        },
        // cacheTime: 10000
    })

    const addSetupIntent = () => {
        mutation.mutate()
        return mutation.data
    }

    const deletePayMethodMutation = useMutation({ 
        mutationFn: async (paymentMethodId: string) => {
            console.log("delete payment method")
            const res = await deletePaymentMethod(paymentMethodId)
            const { deleted } = res
            await reloadPaymentMethods()
            return deleted ?  deleted : false
        },
        // cacheTime: 10000
    })

    const removePaymentMethod = (paymentMethodId: string) => {
        deletePayMethodMutation.mutate(paymentMethodId)
        return deletePayMethodMutation?.data ? mutation.data : ''
    }


    const isLoadingSetupIntent = mutation.isPending || deletePayMethodMutation.isPending
    return { addSetupIntent, removePaymentMethod, isLoadingSetupIntent }
}